import { useParams, Link, Navigate } from 'react-router-dom';
import { useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase, Post } from '@/lib/supabase';
import BlogCard from '../components/BlogCard';
import JsonLd from '@/components/base/JsonLd';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://grainn-office.com';

const categoryLabels: Record<string, string> = {
  web: 'WEB制作',
  sns: 'SNS運用',
  seo: 'SEO・MEO',
  meo: 'MEO対策',
  marketing: 'マーケティング',
  ai: 'AI活用',
};

const categoryColors: Record<string, { bg: string; text: string; border: string }> = {
  web: { bg: 'rgba(30,111,255,0.1)', text: '#1E6FFF', border: 'rgba(30,111,255,0.25)' },
  sns: { bg: 'rgba(0,153,255,0.1)', text: '#0099FF', border: 'rgba(0,153,255,0.25)' },
  seo: { bg: 'rgba(0,180,120,0.1)', text: '#00B478', border: 'rgba(0,180,120,0.25)' },
  meo: { bg: 'rgba(0,180,120,0.1)', text: '#00B478', border: 'rgba(0,180,120,0.25)' },
  marketing: { bg: 'rgba(255,140,0,0.1)', text: '#FF8C00', border: 'rgba(255,140,0,0.25)' },
  ai: { bg: 'rgba(120,80,255,0.1)', text: '#7850FF', border: 'rgba(120,80,255,0.25)' },
};

function postToMock(post: Post) {
  const dateStr = post.published_at ?? post.created_at ?? '';
  const defaultAvatar = 'https://readdy.ai/api/search-image?query=professional%20Japanese%20business%20person%20portrait%20clean%20background&width=100&height=100&seq=avatar-default-001&orientation=squarish';
  return {
    slug: post.slug,
    title: post.title,
    excerpt: post.excerpt,
    content: post.content,
    category: post.category,
    tags: post.tags,
    publishedAt: dateStr ? dateStr.split('T')[0] : '',
    readingTime: post.reading_time,
    coverImage: post.cover_image,
    author: {
      name: post.author_name,
      role: post.author_role || 'GRAINN 代表',
      avatar: post.author_avatar || defaultAvatar,
    },
  };
}

function renderContent(content: string) {
  const lines = content.split('\n');
  const elements: JSX.Element[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={i} className="text-2xl font-bold text-[#0A1628] mt-10 mb-4 pb-3" style={{ borderBottom: '2px solid #E8F0FF' }}>
          {line.replace('## ', '')}
        </h2>
      );
    } else if (line.startsWith('### ')) {
      elements.push(
        <h3 key={i} className="text-lg font-bold text-[#0A1628] mt-7 mb-3">
          {line.replace('### ', '')}
        </h3>
      );
    } else if (line.startsWith('**') && line.endsWith('**')) {
      elements.push(
        <p key={i} className="font-bold text-[#0A1628] mt-4 mb-2">
          {line.replace(/\*\*/g, '')}
        </p>
      );
    } else if (line.startsWith('- ')) {
      const listItems: string[] = [];
      while (i < lines.length && lines[i].startsWith('- ')) {
        listItems.push(lines[i].replace('- ', ''));
        i++;
      }
      elements.push(
        <ul key={`ul-${i}`} className="space-y-2 my-4 pl-4">
          {listItems.map((item, idx) => (
            <li key={idx} className="flex items-start gap-2 text-[#444] text-sm leading-relaxed">
              <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#1E6FFF] flex-shrink-0" />
              <span dangerouslySetInnerHTML={{ __html: item.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
            </li>
          ))}
        </ul>
      );
      continue;
    } else if (/^\d+\. /.test(line)) {
      const listItems: string[] = [];
      while (i < lines.length && /^\d+\. /.test(lines[i])) {
        listItems.push(lines[i].replace(/^\d+\. /, ''));
        i++;
      }
      elements.push(
        <ol key={`ol-${i}`} className="space-y-2 my-4 pl-4">
          {listItems.map((item, idx) => (
            <li key={idx} className="flex items-start gap-3 text-[#444] text-sm leading-relaxed">
              <span
                className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white mt-0.5"
                style={{ background: 'linear-gradient(135deg, #1E6FFF, #0099FF)' }}
              >
                {idx + 1}
              </span>
              <span dangerouslySetInnerHTML={{ __html: item.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
            </li>
          ))}
        </ol>
      );
      continue;
    } else if (line.startsWith('❌') || line.startsWith('✅')) {
      elements.push(
        <p key={i} className="text-sm text-[#444] leading-relaxed my-1.5 pl-1">{line}</p>
      );
    } else if (line.trim() === '') {
      // skip
    } else {
      elements.push(
        <p
          key={i}
          className="text-[#444] text-sm leading-relaxed my-3"
          dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}
        />
      );
    }
    i++;
  }

  return elements;
}

export default function BlogDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<Post | null>(null);
  const [related, setRelated] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      const { data } = await supabase
        .from('posts')
        .select('*')
        .eq('slug', slug)
        .eq('published', true)
        .maybeSingle();

      if (!data) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setPost(data);

      const { data: relatedData } = await supabase
        .from('posts')
        .select('*')
        .eq('published', true)
        .eq('category', data.category)
        .neq('slug', slug)
        .limit(3);

      if (!relatedData || relatedData.length === 0) {
        const { data: fallback } = await supabase
          .from('posts')
          .select('*')
          .eq('published', true)
          .neq('slug', slug)
          .limit(3);
        setRelated(fallback ?? []);
      } else {
        setRelated(relatedData);
      }

      setLoading(false);
    };

    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <main className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#1E6FFF] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-xs text-[#999]">読み込み中...</p>
        </div>
      </main>
    );
  }

  if (notFound || !post) {
    return <Navigate to="/blog" replace />;
  }

  const color = categoryColors[post.category] ?? categoryColors.web;
  const relatedMocks = related.map(postToMock);
  const postMock = postToMock(post);

  const publishedIso = post.published_at ?? post.created_at ?? '';
  const updatedIso = post.updated_at ?? publishedIso;

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        '@id': `${siteUrl}/blog/${post.slug}#article`,
        headline: post.title,
        description: post.excerpt,
        image: post.cover_image,
        datePublished: publishedIso,
        dateModified: updatedIso,
        author: {
          '@type': 'Person',
          name: post.author_name,
          url: `${siteUrl}/about`,
        },
        publisher: {
          '@type': 'Organization',
          name: 'GRAINN',
          url: `${siteUrl}/`,
          logo: {
            '@type': 'ImageObject',
            url: 'https://static.readdy.ai/image/09bb0be54d877515124df82f04d720b0/43564f91c9850eb03002337d577dfbad.jpeg',
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${siteUrl}/blog/${post.slug}`,
        },
        keywords: post.tags.join(', '),
        articleSection: categoryLabels[post.category] ?? post.category,
        inLanguage: 'ja',
        isPartOf: { '@id': `${siteUrl}/blog#webpage` },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'ホーム', item: `${siteUrl}/` },
          { '@type': 'ListItem', position: 2, name: 'ブログ', item: `${siteUrl}/blog` },
          { '@type': 'ListItem', position: 3, name: post.title, item: `${siteUrl}/blog/${post.slug}` },
        ],
      },
    ],
  };

  return (
    <main className="min-h-screen bg-white">
      <title>{`${post.title} | GRAINNブログ`}</title>
      <meta name="description" content={post.excerpt} />
      <meta name="keywords" content={post.tags.join(',')} />
      <link rel="canonical" href={`${siteUrl}/blog/${post.slug}`} />
      <meta property="og:title" content={`${post.title} | GRAINNブログ`} />
      <meta property="og:description" content={post.excerpt} />
      <meta property="og:image" content={post.cover_image} />
      <meta property="og:type" content="article" />
      <meta property="og:url" content={`${siteUrl}/blog/${post.slug}`} />
      <meta property="og:locale" content="ja_JP" />
      <meta property="og:site_name" content="GRAINN" />
      {publishedIso && <meta property="article:published_time" content={publishedIso} />}
      {updatedIso && <meta property="article:modified_time" content={updatedIso} />}
      <meta property="article:author" content={post.author_name} />
      <meta property="article:section" content={categoryLabels[post.category] ?? post.category} />
      {post.tags.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={`${post.title} | GRAINNブログ`} />
      <meta name="twitter:description" content={post.excerpt} />
      <meta name="twitter:image" content={post.cover_image} />
      <meta name="twitter:site" content="@grainn_office" />
      <JsonLd data={articleJsonLd as Record<string, unknown>} id={`blog-${post.slug}`} />

      <Navbar />

      {/* Cover Image */}
      <div className="relative h-[50vh] md:h-[60vh] overflow-hidden">
        <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover object-top" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#020B18]/90 via-[#020B18]/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 max-w-4xl mx-auto px-6 md:px-10 pb-10">
          <div className="flex items-center gap-3 mb-4">
            <span
              className="text-xs px-3 py-1 rounded-full font-medium whitespace-nowrap"
              style={{ background: color.bg, color: color.text, border: `1px solid ${color.border}` }}
            >
              {categoryLabels[post.category]}
            </span>
            <span className="text-white/50 text-xs">{postMock.publishedAt}</span>
            <span className="text-white/50 text-xs">{post.reading_time}分で読める</span>
          </div>
          <h1 className="text-2xl md:text-4xl font-bold text-white leading-snug">{post.title}</h1>
        </div>
      </div>

      {/* Breadcrumb */}
      <div className="border-b border-[#E8F0FF]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-4">
          <nav className="flex items-center gap-2 text-xs text-[#999]" aria-label="パンくずリスト">
            <Link to="/" className="hover:text-[#1E6FFF] transition-colors whitespace-nowrap">ホーム</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/blog" className="hover:text-[#1E6FFF] transition-colors whitespace-nowrap">ブログ</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-[#666] line-clamp-1">{post.title}</span>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-12">
          {/* Article */}
          <article itemScope itemType="https://schema.org/Article">
            <meta itemProp="headline" content={post.title} />
            <meta itemProp="datePublished" content={publishedIso} />
            <meta itemProp="dateModified" content={updatedIso} />
            <meta itemProp="author" content={post.author_name} />
            <div
              className="rounded-xl p-6 mb-8"
              style={{ background: 'rgba(30,111,255,0.05)', border: '1px solid rgba(30,111,255,0.15)' }}
            >
              <p className="text-[#444] text-sm leading-relaxed" itemProp="description">{post.excerpt}</p>
            </div>

            <div className="prose-content">{renderContent(post.content)}</div>

            {/* Tags */}
            <div className="mt-10 pt-8 border-t border-[#E8F0FF]">
              <p className="text-xs text-[#999] mb-3 tracking-widest">タグ</p>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <span key={tag} className="text-xs text-[#666] bg-[#F5F7FA] px-3 py-1.5 rounded-full">#{tag}</span>
                ))}
              </div>
            </div>

            {/* Author */}
            <div
              className="mt-10 rounded-2xl p-6 flex items-center gap-5"
              style={{ background: 'rgba(30,111,255,0.04)', border: '1px solid rgba(30,111,255,0.12)' }}
              itemScope itemType="https://schema.org/Person"
            >
              <div className="w-14 h-14 flex-shrink-0 rounded-full overflow-hidden">
                <img src={postMock.author.avatar} alt={post.author_name} className="w-full h-full object-cover object-top" itemProp="image" />
              </div>
              <div>
                <p className="font-bold text-[#0A1628] text-sm" itemProp="name">{post.author_name}</p>
                <p className="text-[#999] text-xs mt-0.5" itemProp="jobTitle">{post.author_role || 'GRAINN 代表'}</p>
                <p className="text-[#666] text-xs mt-2 leading-relaxed">
                  大阪を拠点にWEBサイト制作・SNS運用代行・MEO対策を一気通貫で提供。
                </p>
              </div>
            </div>

            <div className="mt-10">
              <Link to="/blog" className="inline-flex items-center gap-2 text-[#1E6FFF] text-sm font-semibold hover:gap-3 transition-all">
                <i className="ri-arrow-left-line" />
                ブログ一覧に戻る
              </Link>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <div
                className="rounded-2xl p-6 text-center"
                style={{ background: 'linear-gradient(135deg, #020B18 0%, #0A1E3D 100%)', border: '1px solid rgba(30,111,255,0.2)' }}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-full mx-auto mb-4" style={{ background: 'rgba(30,111,255,0.2)' }}>
                  <i className="ri-chat-3-line text-[#4DA3FF] text-lg" />
                </div>
                <p className="text-white font-bold text-sm mb-2">無料相談受付中</p>
                <p className="text-white/50 text-xs leading-relaxed mb-5">WEB制作・SNS運用・MEO対策のご相談はお気軽に</p>
                <Link
                  to="/contact"
                  className="block text-white text-xs px-5 py-3 rounded-full font-bold transition-all hover:opacity-90 whitespace-nowrap"
                  style={{ background: 'linear-gradient(90deg, #1E6FFF, #0099FF)' }}
                >
                  無料相談を申し込む
                </Link>
              </div>

              {relatedMocks.length > 0 && (
                <div>
                  <p className="text-xs tracking-[0.2em] text-[#999] mb-4 uppercase">関連記事</p>
                  <div className="space-y-4">
                    {relatedMocks.map((rp) => (
                      <Link key={rp.slug} to={`/blog/${rp.slug}`} className="group flex gap-3 cursor-pointer">
                        <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden">
                          <img src={rp.coverImage} alt={rp.title} className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-[#0A1628] leading-snug line-clamp-2 group-hover:text-[#1E6FFF] transition-colors">{rp.title}</p>
                          <p className="text-[#999] text-xs mt-1">{rp.publishedAt}</p>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>

      {/* Related Posts (Mobile) */}
      {relatedMocks.length > 0 && (
        <section className="lg:hidden py-12 bg-[#F8FAFF]">
          <div className="max-w-4xl mx-auto px-6 md:px-10">
            <p className="text-xs tracking-[0.2em] text-[#999] mb-6 uppercase">関連記事</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {relatedMocks.slice(0, 2).map((rp) => (
                <BlogCard key={rp.slug} post={rp} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-10 text-center">
          <p className="text-[#1E6FFF] text-xs tracking-[0.3em] mb-4 uppercase">/ Contact</p>
          <h2 className="text-2xl md:text-3xl font-bold text-[#0A1628] mb-4">ご相談・お問い合わせはお気軽に</h2>
          <p className="text-[#666] text-sm leading-relaxed mb-8">初回相談は無料です。まずはお気軽にお話しください。</p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 text-white px-10 py-4 rounded-full text-sm font-bold tracking-widest transition-all hover:opacity-90 hover:scale-105 whitespace-nowrap"
            style={{ background: 'linear-gradient(90deg, #1E6FFF 0%, #0099FF 100%)' }}
          >
            無料相談を申し込む
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}
